import { Injectable, inject } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import {
  BehaviorSubject,
  Observable,
  Subject,
  catchError,
  debounceTime,
  distinctUntilChanged,
  filter,
  of,
  skip,
  switchMap,
  tap,
  take,
} from 'rxjs';
import { ComponentDataFilterService } from '../component-data-filter/component-data-filter.service';
import { EventsFilterApiService } from './events-filter-api.service';
import { EventsFilterUrlSyncService } from './events-filter-url-sync.service';
import { AuthService } from '../auth/auth.service';
import {
  EventQueryParams,
  EventsFilterCriteria,
  FilterFacetCounts,
  FilterLifecycle,
  FilterMetadata,
  buildHttpQueryFromEventParams,
  buildHttpQueryFromLoadParams,
  buildQueryParamsFromCriteria,
  createEmptyCriteria,
  createEmptyFacetCounts,
} from './events-filter.model';

@Injectable({ providedIn: 'root' })
export class EventsFilterStateService {
  private readonly authService = inject(AuthService);
  private lifecycleSubject = new BehaviorSubject<FilterLifecycle>('idle');
  private metadataSubject = new BehaviorSubject<FilterMetadata | null>(null);
  private criteriaSubject = new BehaviorSubject<EventsFilterCriteria>(createEmptyCriteria());
  private resultCountSubject = new BehaviorSubject<number>(0);
  private facetCountsSubject = new BehaviorSubject<FilterFacetCounts>(createEmptyFacetCounts());
  private queryParamsSubject = new BehaviorSubject<EventQueryParams | null>(null);
  private initPromise: Observable<EventsFilterCriteria> | null = null;
  private criteriaUpdateDebounce$ = new Subject<void>();
  private facetRefresh$ = new Subject<Record<string, string>>();
  readonly lifecycle$ = this.lifecycleSubject.asObservable();
  readonly metadata$ = this.metadataSubject.asObservable();
  readonly criteria$ = this.criteriaSubject.asObservable();
  readonly resultCount$ = this.resultCountSubject.asObservable();
  readonly facetCounts$ = this.facetCountsSubject.asObservable();

  /** Only emits after metadata is ready — avoids empty pre-init params on refresh. */
  readonly resolvedQueryParams$: Observable<EventQueryParams> = this.queryParamsSubject.pipe(
    filter((params): params is EventQueryParams => params !== null),
    distinctUntilChanged((a, b) => JSON.stringify(a) === JSON.stringify(b))
  );

  constructor(
    private api: EventsFilterApiService,
    private urlSync: EventsFilterUrlSyncService,
    private route: ActivatedRoute,
    private filterService: ComponentDataFilterService
  ) {
    this.criteriaUpdateDebounce$.pipe(debounceTime(300)).subscribe(() => {
      this.publishQueryParams();
    });

    this.facetRefresh$.pipe(
      debounceTime(300),
      switchMap((httpQuery) =>
        this.api.getFilterFacets(httpQuery).pipe(
          catchError((err) => {
            console.warn('[EventsFilterState] facet request failed', err);
            return of(createEmptyFacetCounts());
          })
        )
      )
    ).subscribe((counts) => this.facetCountsSubject.next(counts));

    this.authService.isAuthenticated$.pipe(
      distinctUntilChanged(),
      skip(1),
      debounceTime(250)
    ).subscribe(() => {
      this.reloadForAuthChange().subscribe();
    });
  }
  get lifecycle(): FilterLifecycle {
    return this.lifecycleSubject.value;
  }

  get metadata(): FilterMetadata | null {
    return this.metadataSubject.value;
  }

  get criteria(): EventsFilterCriteria {
    return this.criteriaSubject.value;
  }

  initialize(): Observable<EventsFilterCriteria> {
    if (this.lifecycleSubject.value === 'ready') {
      return of(this.criteria);
    }
    if (this.initPromise) {
      return this.initPromise;
    }

    this.initPromise = this.fetchMetadataAndApplyCriteria(false);
    return this.initPromise;
  }

  /** Re-fetch metadata and visibility after login/logout without a full page reload. */
  reloadForAuthChange(): Observable<EventsFilterCriteria> {
    this.initPromise = null;
    this.queryParamsSubject.next(null);
    this.facetCountsSubject.next(createEmptyFacetCounts());
    this.resultCountSubject.next(0);
    return this.fetchMetadataAndApplyCriteria(true).pipe(
      tap(() => this.emitAuthFilterChanged())
    );
  }

  private fetchMetadataAndApplyCriteria(preserveUserFilters: boolean): Observable<EventsFilterCriteria> {
    this.lifecycleSubject.next('loading-metadata');

    const snapshot = this.route.snapshot.queryParams as Record<string, string | undefined>;
    const metadataOrgIds = this.organizationIdsQueryFromSnapshot(snapshot);

    return this.api.getFilterMetadata(
      metadataOrgIds ? { organization_ids: metadataOrgIds } : undefined
    ).pipe(
      catchError(() =>
        of({
          organizations: [],
          defaultOrganizationIds: [],
          educationalPaths: [],
          defaultEducationalPathIds: [],
          modules: [],
          locations: [],
          defaultLocationGuids: [],
          defaults: { eventTypes: ['education', 'course', 'activity'], hideSubEvents: true },
          visibility: {
            showExercises: false,
            showEducationalPaths: false,
            showModules: true,
            showLocations: false,
            showDistance: false,
            showHideFull: false,
            showHideParticipated: false,
            showGroupCalendarEvents: false,
            superadminGroupCalendarFilter: false,
          },
        } as FilterMetadata)
      ),
      switchMap((metadata) => {
        this.metadataSubject.next(metadata);
        const hasUrl = this.urlSync.hasFilterParams(snapshot);
        let criteria: EventsFilterCriteria;
        if (preserveUserFilters && !hasUrl) {
          criteria = this.applyAuthAwareCriteriaUpdate(metadata, this.criteriaSubject.value);
        } else {
          criteria = this.urlSync.applyDefaultsFromMetadata(metadata, snapshot);
        }
        criteria = this.ensureDefaultOrganizationIds(metadata, criteria);
        this.criteriaSubject.next(criteria);
        this.syncToLegacyFilter(criteria);
        this.lifecycleSubject.next('ready');
        this.publishQueryParams();
        this.initPromise = null;
        return of(criteria);
      })
    );
  }

  private organizationIdsQueryFromSnapshot(
    snapshot: Record<string, string | undefined>
  ): string | undefined {
    const raw = snapshot['orgIds'] || snapshot['org'];
    if (!raw || !/^\d/.test(raw)) {
      return undefined;
    }
    const ids = raw
      .split(',')
      .map((id) => parseInt(id, 10))
      .filter((id) => !isNaN(id));
    return ids.length ? ids.join(',') : undefined;
  }

  private organizationIdsQueryFromCriteria(organizationIds: number[]): string | undefined {
    return organizationIds.length ? organizationIds.join(',') : undefined;
  }

  private emitAuthFilterChanged(): void {
    if (typeof window !== 'undefined') {
      window.dispatchEvent(new CustomEvent('duna-filter-auth-changed'));
    }
  }

  private applyAuthAwareCriteriaUpdate(
    metadata: FilterMetadata,
    current: EventsFilterCriteria
  ): EventsFilterCriteria {
    const allowedTypes = metadata.defaults?.eventTypes || ['education', 'course', 'activity'];
    let eventType = (current.eventType || []).filter((t) => allowedTypes.includes(t));
    if (eventType.length === 0) {
      eventType = [...allowedTypes];
    }

    const v = metadata.visibility;
    const empty = createEmptyCriteria();

    return {
      ...current,
      eventType,
      organizationIds: current.organizationIds?.length
        ? current.organizationIds
        : [...(metadata.defaultOrganizationIds || [])],
      // Role-aware defaults from metadata: restricted roles get [], others get enrolled paths.
      educationalPathIds: v?.showEducationalPaths
        ? [...(metadata.defaultEducationalPathIds || [])]
        : [],
      educationalPathVersionIds: [],
      moduleIds: v?.showModules ? current.moduleIds : [],
      locationGuids: v?.showLocations
        ? (current.locationGuids?.length
            ? current.locationGuids
            : [...(metadata.defaultLocationGuids || [])])
        : [],
      hideFullEvents: v?.showHideFull ? current.hideFullEvents : false,
      hideParticipatedEvents: v?.showHideParticipated ? current.hideParticipatedEvents : false,
      showGroupCalendarEvents: false,
      distanceFilter: v?.showDistance ? current.distanceFilter : { ...empty.distanceFilter },
    };
  }

  waitUntilReady(): Observable<EventsFilterCriteria> {
    if (this.lifecycleSubject.value === 'ready') {
      return of(this.criteria);
    }
    return this.initialize();
  }

  /** Reset filter fields to the same defaults as initial load (incl. user org default). */
  resetToDefaults(): void {
    const metadata = this.metadataSubject.value;
    if (!metadata || this.lifecycleSubject.value !== 'ready') {
      return;
    }

    const criteria = this.buildResetCriteriaFromMetadata(metadata);
    this.replaceCriteria(criteria);
  }

  private buildResetCriteriaFromMetadata(
    metadata: FilterMetadata
  ): EventsFilterCriteria {
    const empty = createEmptyCriteria();
    const v = metadata.visibility;
    let eventType = [...(metadata.defaults?.eventTypes || ['education', 'course', 'activity'])];
    if (!v?.showExercises) {
      eventType = eventType.filter((t) => t !== 'exercise');
    }

    const criteria: EventsFilterCriteria = {
      ...empty,
      eventType,
      hideSubEvents: metadata.defaults?.hideSubEvents !== false,
      // «Ryd filter» clears user-chosen filters; only organisation keeps its default.
      educationalPathIds: [],
      educationalPathVersionIds: [],
      moduleIds: [],
      locationGuids: [],
      showGroupCalendarEvents: false,
    };

    return this.ensureDefaultOrganizationIds(metadata, criteria);
  }

  private ensureDefaultOrganizationIds(
    metadata: FilterMetadata,
    criteria: EventsFilterCriteria
  ): EventsFilterCriteria {
    const organizationIds = this.resolveDefaultOrganizationIds(metadata);
    if (!organizationIds.length) {
      return criteria;
    }
    return {
      ...criteria,
      organizationIds,
    };
  }

  private resolveDefaultOrganizationIds(metadata: FilterMetadata): number[] {
    const fromMetadata = (metadata.defaultOrganizationIds || [])
      .map((id) => Number(id))
      .filter((id) => !isNaN(id) && id > 0);
    if (fromMetadata.length) {
      return fromMetadata;
    }

    const user = this.authService.getCurrentUser() as
      | { organizations_id?: number | string; organizationsId?: number | string }
      | null;
    const raw = user?.organizations_id ?? user?.organizationsId;
    const userOrgId = raw != null ? Number(raw) : 0;
    return !isNaN(userOrgId) && userOrgId > 0 ? [userOrgId] : [];
  }

  private replaceCriteria(criteria: EventsFilterCriteria): void {
    this.lifecycleSubject.next('applying');
    this.criteriaSubject.next(criteria);
    this.syncToLegacyFilter(criteria);
    this.urlSync.syncToUrl(criteria);
    this.lifecycleSubject.next('ready');
    this.criteriaUpdateDebounce$.next();
  }

  updateCriteria(partial: Partial<EventsFilterCriteria>, syncUrl = true): void {
    // Allow updates while 'applying' so nested/echoed filter changes (e.g. path clear
    // while another filter instance reacts) are not silently dropped.
    if (this.lifecycleSubject.value !== 'ready' && this.lifecycleSubject.value !== 'applying') {
      return;
    }

    const next = { ...this.criteriaSubject.value, ...partial };
    const guestOrgChange =
      !this.authService.isAuthenticated &&
      partial.organizationIds !== undefined;

    if (guestOrgChange) {
      this.lifecycleSubject.next('applying');
      const orgQuery = this.organizationIdsQueryFromCriteria(next.organizationIds || []);
      this.api
        .getFilterMetadata(orgQuery ? { organization_ids: orgQuery } : undefined)
        .pipe(
          take(1),
          catchError(() => of(null))
        )
        .subscribe((fresh) => {
          let criteria = next;
          if (fresh) {
            const current = this.metadataSubject.value;
            if (current) {
              this.metadataSubject.next({ ...current, modules: fresh.modules });
            }
            const validModuleIds = new Set(fresh.modules.map((m) => m.module_id));
            criteria = {
              ...next,
              moduleIds: (next.moduleIds || []).filter((id) => validModuleIds.has(id)),
            };
          }
          this.commitCriteriaUpdate(criteria, syncUrl);
        });
      return;
    }

    this.commitCriteriaUpdate(next, syncUrl);
  }

  private commitCriteriaUpdate(criteria: EventsFilterCriteria, syncUrl: boolean): void {
    this.lifecycleSubject.next('applying');
    this.criteriaSubject.next(criteria);
    this.syncToLegacyFilter(criteria);
    if (syncUrl) {
      this.urlSync.syncToUrl(criteria);
    }
    this.lifecycleSubject.next('ready');
    this.criteriaUpdateDebounce$.next();
  }

  patchCriteria(mutator: (current: EventsFilterCriteria) => EventsFilterCriteria, syncUrl = true): void {
    this.updateCriteria(mutator(this.criteria), syncUrl);
  }

  setResultCount(count: number): void {
    this.resultCountSubject.next(count);
  }

  setFacetCounts(counts: FilterFacetCounts): void {
    this.facetCountsSubject.next(counts);
  }

  refreshFacets(dateRange?: { startDate?: string; endDate?: string }): void {
    if (this.lifecycleSubject.value !== 'ready') {
      return;
    }
    const params = buildQueryParamsFromCriteria(this.criteria);
    if (dateRange?.startDate) {
      params.startDate = dateRange.startDate;
    }
    if (dateRange?.endDate) {
      params.endDate = dateRange.endDate;
    }
    this.facetRefresh$.next(buildHttpQueryFromEventParams(params));
  }

  /** Refresh facets using the exact query params sent to GET /events. */
  refreshFacetsFromLoadParams(loadParams: Record<string, unknown>): void {
    if (this.lifecycleSubject.value !== 'ready') {
      return;
    }
    const httpQuery = buildHttpQueryFromLoadParams(loadParams);
    if (this.criteria.hideSubEvents) {
      httpQuery['exclude_sub_events'] = 'true';
    }
    if (!httpQuery['startDate'] || !httpQuery['endDate']) {
      return;
    }
    this.facetRefresh$.next(httpQuery);
  }

  get facetCounts(): FilterFacetCounts {
    return this.facetCountsSubject.value;
  }

  buildQueryParams(criteria?: EventsFilterCriteria): EventQueryParams {
    return buildQueryParamsFromCriteria(criteria ?? this.criteria);
  }

  private publishQueryParams(): void {
    if (this.lifecycleSubject.value !== 'ready') {
      return;
    }
    this.queryParamsSubject.next(buildQueryParamsFromCriteria(this.criteriaSubject.value));
  }

  private syncToLegacyFilter(criteria: EventsFilterCriteria): void {
    const legacy = this.filterService.getFilter('events-filter');
    if (legacy?.setFilter) {
      legacy.setFilter({
        eventType: criteria.eventType,
        dateRange: criteria.dateRange,
        visibility: criteria.visibility,
        searchText: criteria.searchText,
        organizationIds: criteria.organizationIds,
        hideSubEvents: criteria.hideSubEvents,
        educationalPathIds: criteria.educationalPathIds,
        moduleIds: criteria.moduleIds,
        educationalPathVersionIds: criteria.educationalPathVersionIds,
        locationGuids: criteria.locationGuids,
        hideFullEvents: criteria.hideFullEvents,
        hideParticipatedEvents: criteria.hideParticipatedEvents,
        showGroupCalendarEvents: criteria.showGroupCalendarEvents,
        distanceFilter: criteria.distanceFilter,
      });
    }
  }
}
